import { useState, useEffect, useMemo } from 'react';
import { Search, Download, Loader2, CalendarDays } from 'lucide-react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { useCommunityContext } from '@/providers/community-context';
import { triggerTextFileDownload } from '@/lib/trigger-download';

interface MemberReportRow {
  memberId: string;
  memberName: string;
  leadsGiven: number;
  leadsReceived: number;
  thanksGiven: number;
  thanksGivenAmount: number;
  thanksReceived: number;
  thanksReceivedAmount: number;
  testimonialsGiven: number;
  testimonialsReceived: number;
  meetupsInitiated: number;
  meetupsParticipated: number;
}

interface BusinessReportResponse {
  rows: MemberReportRow[];
}

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

function formatAmount(count: number, amount: number) {
  if (count === 0) return '0';
  return `${count} (Rs. ${amount.toLocaleString('en-IN')})`;
}

interface BusinessReportViewProps {
  timeFilter: string;
}

export function BusinessReportView({ timeFilter }: BusinessReportViewProps) {
  const { communityId } = useParams<{ communityId: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const { community: currentCommunity } = useCommunityContext();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const queryParams = new URLSearchParams();
  if (timeFilter) queryParams.set('timeFilter', timeFilter);
  if (fromDate) queryParams.set('fromDate', fromDate);
  if (toDate) queryParams.set('toDate', toDate);
  if (debouncedSearch) queryParams.set('search', debouncedSearch);

  const { data, isLoading, isValidating } = useSWR<BusinessReportResponse>(
    communityId
      ? `/api/communities/${communityId}/business-report?${queryParams.toString()}`
      : null,
    fetcher,
    { keepPreviousData: true }
  );

  const rows = useMemo(() => {
    return data?.rows ?? [];
  }, [data]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        leadsGiven: acc.leadsGiven + row.leadsGiven,
        leadsReceived: acc.leadsReceived + row.leadsReceived,
        thanksGiven: acc.thanksGiven + row.thanksGiven,
        thanksGivenAmount: acc.thanksGivenAmount + row.thanksGivenAmount,
        thanksReceived: acc.thanksReceived + row.thanksReceived,
        thanksReceivedAmount: acc.thanksReceivedAmount + row.thanksReceivedAmount,
        testimonialsGiven: acc.testimonialsGiven + row.testimonialsGiven,
        testimonialsReceived: acc.testimonialsReceived + row.testimonialsReceived,
        meetupsInitiated: acc.meetupsInitiated + row.meetupsInitiated,
        meetupsParticipated: acc.meetupsParticipated + row.meetupsParticipated,
      }),
      {
        leadsGiven: 0,
        leadsReceived: 0,
        thanksGiven: 0,
        thanksGivenAmount: 0,
        thanksReceived: 0,
        thanksReceivedAmount: 0,
        testimonialsGiven: 0,
        testimonialsReceived: 0,
        meetupsInitiated: 0,
        meetupsParticipated: 0,
      }
    );
  }, [rows]);

  const handleDownloadCSV = () => {
    const headers: string[] = ['Member'];

    if (currentCommunity.isLeadEnabled) {
      headers.push('Leads Given', 'Leads Received');
    }
    if (currentCommunity.isThankYouNoteEnabled) {
      headers.push('Thank You Given', 'Thank You Received');
    }
    if (currentCommunity.isTestimonialEnabled) {
      headers.push('Testimonial Given', 'Testimonial Received');
    }
    if (currentCommunity.isMeetupEnabled) {
      headers.push('Meetup Initiated', 'Meetup Participated');
    }

    const csvRows = [
      headers.join(','),
      ...rows.map((r) => {
        const values: (string | number)[] = [`"${r.memberName}"`];
        if (currentCommunity.isLeadEnabled) {
          values.push(r.leadsGiven, r.leadsReceived);
        }
        if (currentCommunity.isThankYouNoteEnabled) {
          values.push(
            `"${formatAmount(r.thanksGiven, r.thanksGivenAmount)}"`,
            `"${formatAmount(r.thanksReceived, r.thanksReceivedAmount)}"`
          );
        }
        if (currentCommunity.isTestimonialEnabled) {
          values.push(r.testimonialsGiven, r.testimonialsReceived);
        }
        if (currentCommunity.isMeetupEnabled) {
          values.push(r.meetupsInitiated, r.meetupsParticipated);
        }
        return values.join(',');
      }),
      (() => {
        const values: (string | number)[] = ['"Total"'];
        if (currentCommunity.isLeadEnabled) {
          values.push(totals.leadsGiven, totals.leadsReceived);
        }
        if (currentCommunity.isThankYouNoteEnabled) {
          values.push(
            `"${formatAmount(totals.thanksGiven, totals.thanksGivenAmount)}"`,
            `"${formatAmount(totals.thanksReceived, totals.thanksReceivedAmount)}"`
          );
        }
        if (currentCommunity.isTestimonialEnabled) {
          values.push(totals.testimonialsGiven, totals.testimonialsReceived);
        }
        if (currentCommunity.isMeetupEnabled) {
          values.push(totals.meetupsInitiated, totals.meetupsParticipated);
        }
        return values.join(',');
      })(),
    ];

    triggerTextFileDownload(
      `business-report-${new Date().toISOString().split('T')[0]}.csv`,
      csvRows.join('\n')
    );
  };

  if (isLoading && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground font-medium animate-pulse">Loading report...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm w-full">
          {isValidating && data ? (
            <Loader2 className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground animate-spin" />
          ) : (
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          )}
          <Input
            type="search"
            placeholder="Search by name, phone, or email..."
            className="pl-8 bg-card"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          className="gap-2 shrink-0"
          onClick={handleDownloadCSV}
          disabled={rows.length === 0}
        >
          <Download className="h-4 w-4" />
          Download CSV
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm text-muted-foreground font-medium whitespace-nowrap">From</span>
          <Input
            type="date"
            className="w-[160px] bg-card"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground font-medium whitespace-nowrap">To</span>
          <Input
            type="date"
            className="w-[160px] bg-card"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        {(fromDate || toDate) && (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => {
              setFromDate('');
              setToDate('');
            }}
          >
            Clear dates
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-border/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead rowSpan={2} className="font-bold text-foreground border-r align-bottom">
                Member
              </TableHead>
              {currentCommunity.isLeadEnabled && (
                <TableHead colSpan={2} className="text-center font-bold text-foreground border-r">
                  Leads
                </TableHead>
              )}
              {currentCommunity.isThankYouNoteEnabled && (
                <TableHead colSpan={2} className="text-center font-bold text-foreground border-r">
                  Thank You Note
                </TableHead>
              )}
              {currentCommunity.isTestimonialEnabled && (
                <TableHead colSpan={2} className="text-center font-bold text-foreground border-r">
                  Testimonial
                </TableHead>
              )}
              {currentCommunity.isMeetupEnabled && (
                <TableHead colSpan={2} className="text-center font-bold text-foreground">
                  Meetup
                </TableHead>
              )}
            </TableRow>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              {currentCommunity.isLeadEnabled && (
                <TableHead className="text-center text-xs font-medium border-r">Given</TableHead>
              )}
              {currentCommunity.isLeadEnabled && (
                <TableHead className="text-center text-xs font-medium border-r">Received</TableHead>
              )}
              {currentCommunity.isThankYouNoteEnabled && (
                <TableHead className="text-center text-xs font-medium border-r">Given</TableHead>
              )}
              {currentCommunity.isThankYouNoteEnabled && (
                <TableHead className="text-center text-xs font-medium border-r">Received</TableHead>
              )}
              {currentCommunity.isTestimonialEnabled && (
                <TableHead className="text-center text-xs font-medium border-r">Given</TableHead>
              )}
              {currentCommunity.isTestimonialEnabled && (
                <TableHead className="text-center text-xs font-medium border-r">Received</TableHead>
              )}
              {currentCommunity.isMeetupEnabled && (
                <TableHead className="text-center text-xs font-medium border-r">
                  Initiated
                </TableHead>
              )}
              {currentCommunity.isMeetupEnabled && (
                <TableHead className="text-center text-xs font-medium">Participated</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                  {debouncedSearch
                    ? 'No members found matching your search.'
                    : 'No report data available.'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.memberId}>
                  <TableCell className="font-medium border-r whitespace-nowrap">
                    {row.memberName}
                  </TableCell>
                  {currentCommunity.isLeadEnabled && (
                    <>
                      <TableCell className="text-center border-r">
                        {row.leadsGiven == 0 ? '-' : row.leadsGiven}
                      </TableCell>
                      <TableCell className="text-center border-r">
                        {row.leadsReceived == 0 ? '-' : row.leadsReceived}
                      </TableCell>
                    </>
                  )}
                  {currentCommunity.isThankYouNoteEnabled && (
                    <>
                      <TableCell className="text-center border-r text-xs whitespace-nowrap">
                        {row.thanksGiven == 0
                          ? '-'
                          : formatAmount(row.thanksGiven, row.thanksGivenAmount)}
                      </TableCell>
                      <TableCell className="text-center border-r text-xs whitespace-nowrap">
                        {row.thanksReceived == 0
                          ? '-'
                          : formatAmount(row.thanksReceived, row.thanksReceivedAmount)}
                      </TableCell>
                    </>
                  )}
                  {currentCommunity.isTestimonialEnabled && (
                    <>
                      <TableCell className="text-center border-r">
                        {row.testimonialsGiven == 0 ? '-' : row.testimonialsGiven}
                      </TableCell>
                      <TableCell className="text-center border-r">
                        {row.testimonialsReceived == 0 ? '-' : row.testimonialsReceived}
                      </TableCell>
                    </>
                  )}
                  {currentCommunity.isMeetupEnabled && (
                    <>
                      <TableCell className="text-center border-r">
                        {row.meetupsInitiated == 0 ? '-' : row.meetupsInitiated}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.meetupsParticipated == 0 ? '-' : row.meetupsParticipated}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
          {rows.length > 0 && (
            <TableFooter>
              <TableRow className="bg-muted/40 font-bold">
                <TableCell className="border-r">Total business done:</TableCell>
                {currentCommunity.isLeadEnabled && (
                  <>
                    <TableCell className="text-center border-r">
                      {totals.leadsGiven == 0 ? '-' : totals.leadsGiven}
                    </TableCell>
                    <TableCell className="text-center border-r">
                      {totals.leadsReceived == 0 ? '-' : totals.leadsReceived}
                    </TableCell>
                  </>
                )}
                {currentCommunity.isThankYouNoteEnabled && (
                  <>
                    <TableCell className="text-center border-r text-xs whitespace-nowrap">
                      {totals.thanksGiven == 0
                        ? '-'
                        : formatAmount(totals.thanksGiven, totals.thanksGivenAmount)}
                    </TableCell>
                    <TableCell className="text-center border-r text-xs whitespace-nowrap">
                      {totals.thanksReceived == 0
                        ? '-'
                        : formatAmount(totals.thanksReceived, totals.thanksReceivedAmount)}
                    </TableCell>
                  </>
                )}
                {currentCommunity.isTestimonialEnabled && (
                  <>
                    <TableCell className="text-center border-r">
                      {totals.testimonialsGiven == 0 ? '-' : totals.testimonialsGiven}
                    </TableCell>
                    <TableCell className="text-center border-r">
                      {totals.testimonialsReceived == 0 ? '-' : totals.testimonialsReceived}
                    </TableCell>
                  </>
                )}
                {currentCommunity.isMeetupEnabled && (
                  <>
                    <TableCell className="text-center border-r">
                      {totals.meetupsInitiated == 0 ? '-' : totals.meetupsInitiated}
                    </TableCell>
                    <TableCell className="text-center">
                      {totals.meetupsParticipated == 0 ? '-' : totals.meetupsParticipated}
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
}
